import { AnnaError } from "@anna-money/anna-web-lib/utils";

// The declarative statement the customer is agreeing to when they sign. It is
// the single source of truth for the wording: the signature pad renders it on
// screen (so the customer sees exactly what they are signing) and the evidence
// image below reproduces it, so the file on record shows what the signature was
// for — not a signature on its own.
export const ASIC_AGENT_NOMINATION_STATEMENT =
  "I nominate ANNA as my ASIC agent. As my ASIC agent, ANNA can manage my company's ASIC obligations on my " +
  "behalf, including lodging documents and handling correspondence with ASIC.";

export interface AsicNominationEvidenceContext {
  // Full company name (name + postfix), when known, so the record ties the
  // nomination to a specific company.
  companyName?: string;
  // Human-readable "signed at" timestamp, e.g. "22 July 2026, 2:03 PM".
  signedAtLabel: string;
}

// Rendered at twice the logical size so the resulting PNG stays crisp when
// viewed or printed as evidence.
const SCALE = 2;
const WIDTH = 600;
const PADDING = 24;
const CONTENT_WIDTH = WIDTH - PADDING * 2;

const TITLE = "ASIC agent nomination";
const TITLE_FONT = "600 20px sans-serif";
const BODY_FONT = "14px sans-serif";
const LABEL_FONT = "600 13px sans-serif";
const TITLE_LINE_HEIGHT = 28;
const BODY_LINE_HEIGHT = 20;

const INK = "#1a1a1a";
const MUTED = "#4a4a4a";
const BORDER = "#c9c9c9";

/**
 * Splits `text` into lines that each fit within `maxWidth`, using the supplied
 * `measure` function to size candidate lines. Kept pure (measurement injected)
 * so the wrapping logic can be unit tested without a real canvas.
 */
export function wrapText(text: string, maxWidth: number, measure: (line: string) => number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && measure(candidate) > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }

  if (current) {
    lines.push(current);
  }

  return lines;
}

/**
 * Composes the ASIC agent nomination evidence image: the nomination statement,
 * the company it applies to, the customer's signature and the time it was
 * signed, all on a single white PNG. Returns a `data:image/png` URL ready to be
 * turned into a file and uploaded. Rejects if the signature cannot be rendered.
 */
export async function buildAsicNominationEvidence(
  signatureDataUrl: string,
  context: AsicNominationEvidenceContext,
): Promise<string> {
  const signature = await loadImage(signatureDataUrl);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new AnnaError("Could not get a 2D canvas context for the nomination evidence");
  }

  // Lay the content out top to bottom, measuring the wrapped statement and the
  // scaled signature so the canvas is exactly tall enough for everything.
  ctx.font = BODY_FONT;
  const statementLines = wrapText(
    ASIC_AGENT_NOMINATION_STATEMENT,
    CONTENT_WIDTH,
    (line) => ctx.measureText(line).width,
  );
  // Wrapped like the statement rather than drawn as a single line: the canvas is
  // a fixed width, so a long company name would otherwise run off the edge and
  // be silently cut out of the evidence.
  const companyLines = context.companyName
    ? wrapText(`Company: ${context.companyName}`, CONTENT_WIDTH, (line) => ctx.measureText(line).width)
    : [];

  const signatureWidth = CONTENT_WIDTH;
  const signatureHeight = signature.height
    ? signature.width
      ? (signatureWidth * signature.height) / signature.width
      : 200
    : 200;

  const height =
    PADDING +
    TITLE_LINE_HEIGHT +
    12 +
    statementLines.length * BODY_LINE_HEIGHT +
    (companyLines.length ? 16 + companyLines.length * BODY_LINE_HEIGHT : 0) +
    20 +
    BODY_LINE_HEIGHT +
    signatureHeight +
    16 +
    BODY_LINE_HEIGHT +
    PADDING;

  // Sizing the canvas resets its context, so apply the scale and redraw after.
  canvas.width = WIDTH * SCALE;
  canvas.height = height * SCALE;
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, WIDTH, height);
  ctx.textBaseline = "top";

  let y = PADDING;

  ctx.fillStyle = INK;
  ctx.font = TITLE_FONT;
  ctx.fillText(TITLE, PADDING, y);
  y += TITLE_LINE_HEIGHT + 12;

  ctx.font = BODY_FONT;
  for (const line of statementLines) {
    ctx.fillText(line, PADDING, y);
    y += BODY_LINE_HEIGHT;
  }

  if (companyLines.length) {
    y += 16;
    ctx.fillStyle = MUTED;
    for (const line of companyLines) {
      ctx.fillText(line, PADDING, y);
      y += BODY_LINE_HEIGHT;
    }
  }

  y += 20;
  ctx.fillStyle = MUTED;
  ctx.font = LABEL_FONT;
  ctx.fillText("Signature", PADDING, y);
  y += BODY_LINE_HEIGHT;

  ctx.strokeStyle = BORDER;
  ctx.strokeRect(PADDING, y, signatureWidth, signatureHeight);
  ctx.drawImage(signature, PADDING, y, signatureWidth, signatureHeight);
  y += signatureHeight + 16;

  ctx.fillStyle = MUTED;
  ctx.font = BODY_FONT;
  ctx.fillText(`Signed: ${context.signedAtLabel}`, PADDING, y);

  return canvas.toDataURL("image/png");
}

async function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve(image);
    };
    image.onerror = () => {
      reject(new AnnaError("Could not load the signature image for the nomination evidence"));
    };
    image.src = dataUrl;
  });
}
